package com.dynamicruntime.webapp

import com.dynamicruntime.common.endpoint.EI
import com.dynamicruntime.common.endpoint.EP
import com.dynamicruntime.common.schema.SCH
import com.dynamicruntime.common.schema.parseDeliveredLayouts
import com.dynamicruntime.common.util.jsonMap
import com.dynamicruntime.common.util.toJsonStr
import com.dynamicruntime.common.util.toJsonMapOrEmpty
import com.dynamicruntime.common.util.toJsonListOfMaps
import com.dynamicruntime.common.util.toJsonListOfStrings

/**
 * The runtime's schema-catalog endpoints, under the `kda` API context root. In dev the webpack server proxies
 * `/kda` to the runtime on :7070 (see build.gradle.kts), so the calls are same-origin and need no CORS.
 */
private val schemaBase: String get() = apiContextRoot + "/schema"

/** The API context root every runtime endpoint is served under (proxied to :7070 in dev). */
private val apiRoot: String get() = apiContextRoot

/**
 * Fetches endpoint schema from the runtime's `/schema/endpoints` catalog and parses it into the [Catalog]
 * model. Responses are parsed with the shared kernel's [jsonMap] (the same JSON parser the backend uses), so
 * numbers arrive as the kernel's own `Long`/`Double` — matching what the kernel validator expects — instead
 * of JS doubles.
 */
object SchemaCatalogApi {
    /**
     * GET the whole catalog: every registered endpoint's rendering plus the shared `$defs`. An optional
     * [namespace] narrows the results; an optional [client] shows *that* client's surface -- its endpoints and
     * its `$defs` (issues #387, #394), for a caller who holds `allClients`. Null [client] is the caller's own.
     * No `limit` is sent: the listing's default (`defaultEndpointListLimit`) is the whole catalog, which the page
     * shows and filters -- deriving the tag-filter options or `findFormCreateEndpoint` from a truncated page would
     * silently drop endpoints past the cut (issue #489).
     */
    suspend fun fetchCatalog(namespace: String? = null, client: String? = null): Catalog {
        val query = queryString(buildMap {
            namespace?.let { put(EI.namespace, it) }
            client?.let { put(EI.client, it) }
        })
        val results = getJson("$schemaBase/endpoints$query")[EP.results].toJsonMapOrEmpty()
        return toCatalog(results)
    }

    /**
     * GET a single endpoint in the same shape as the full catalog. With [resolveClient] set, [path] is a
     * **bare** path the backend resolves to the surface's client-scoped copy (issue #552) -- how a page
     * that holds only the bare path (e.g. `GEP.formDocCreate`) fetches its one endpoint's closure without
     * first fetching the whole catalog to discover the concrete path. Without it, [path] must be exact.
     *
     * An optional [client] looks the endpoint up on **that** client's surface (issue #714), as [fetchCatalog]'s
     * does: the copy resolved to and the `$defs` it is rendered against are that client's, for an `allClients`
     * caller working in another client's rules. Null is the caller's own. The backend owns the fallback -- a
     * client that varies nothing answers with the shared endpoint -- which is why a page never forms a
     * `/gedra/<client>/…` path itself.
     */
    suspend fun fetchEndpoint(method: String, path: String, resolveClient: Boolean = false, client: String? = null): Catalog {
        val resolve = if (resolveClient) "&${EI.resolveClient}=true" else ""
        val forClient = client?.let { "&${EI.client}=${encodeUriComponent(it)}" } ?: ""
        val results = getJson(
            "$schemaBase/endpoint?${EI.method}=$method&${EI.path}=${encodeUriComponent(path)}$resolve$forClient",
        )[EP.results].toJsonMapOrEmpty()
        return toCatalog(results)
    }

    private fun toCatalog(results: Map<String, Any?>): Catalog = parseCatalog(results)

    /**
     * Executes [endpoint] with the (already coerced) [body] and returns its parsed response envelope. A GET
     * or DELETE carries the fields in the query string (a nested value is JSON-encoded, which the runtime's
     * coercion reparses); a POST/PUT sends them as a JSON body, serialized with the shared kernel's
     * [toJsonStr]. A non-2xx response raises the runtime's error `message`.
     *
     * DELETE joins the query-string side rather than the body side deliberately: DELETE bodies are legal but
     * poorly handled by intermediaries, so nothing here sends one (issue #335).
     */
    suspend fun invoke(
        endpoint: EndpointInfo,
        body: Map<String, Any?>,
        multipart: Boolean = false,
    ): Map<String, Any?> {
        val url = apiRoot + endpoint.path
        var traceId = ""
        val response = if (endpoint.method == "GET" || endpoint.method == "DELETE") {
            // The method is set explicitly rather than left to fetch's GET default, since DELETE takes the
            // same query-string treatment but is not the default.
            val init: dynamic = js("({})")
            init.method = endpoint.method
            val headers: dynamic = js("({})")
            traceId = applyRequestHeaders(headers)
            init.headers = headers
            fetchCompleted(endpoint.method, url + queryString(body), init).orThrow()
        } else {
            val init: dynamic = js("({})")
            init.method = endpoint.method
            val headers: dynamic = js("({})")
            traceId = applyRequestHeaders(headers)
            if (multipart) {
                // An upload: the body is form parts, one of them the file itself. Deliberately no
                // Content-Type header -- the browser must set it, because only it knows the multipart
                // boundary it generated. Setting it by hand here produces a body the server cannot parse.
                init.headers = headers
                init.body = formDataOf(body)
            } else {
                headers["Content-Type"] = "application/json"
                init.headers = headers
                init.body = body.toJsonStr(compact = true)
            }
            fetchCompleted(endpoint.method, url, init).orThrow()
        }
        val map = jsonBody(endpoint.method, url, response.text).orThrow()
        if (!(response.response.ok as Boolean)) {
            // Carry the error up as a structured ApiError (issue #111), the same as `Http` does, so a display
            // site (`userFacingError`) shows the real message -- a 400 with "no such user in your access" reads
            // as that, not as the "server could not be reached" that a bare throwable is treated as.
            throw ApiError(
                message = map[EP.errorMessage] as? String
                    ?: "${endpoint.method} $url failed with status ${response.response.status}",
                fromFragment = map[EP.errorFromFragment] == true,
                status = (map[EP.status] as? Number)?.toInt() ?: (response.response.status as? Number)?.toInt(),
                errorCode = map[EP.errorCode] as? String,
                traceId = traceId,
            )
        }
        return map
    }

    /**
     * The URL a file-download endpoint's content is at, for handing to the browser to fetch itself.
     *
     * A download is deliberately *not* run through [invoke]: the response is bytes, so parsing it as JSON
     * would corrupt it, and holding a file in memory to re-offer it would throw away the `Content-Disposition`
     * the server already sent. Letting the browser navigate to the URL is what makes that header do its job.
     */
    fun downloadUrl(endpoint: EndpointInfo, body: Map<String, Any?>): String = Http.apiUrl(endpoint.path, body)

    private suspend fun getJson(url: String): Map<String, Any?> {
        val init: dynamic = js("({})")
        val headers: dynamic = js("({})")
        // The standard request headers (issue #105, #517), so the catalog's own GETs carry the trace id and the
        // env-debug `_debug` tag exactly as `Http` does -- the two tags this box is most useful for reach here.
        applyRequestHeaders(headers)
        init.headers = headers
        val fetched = fetchCompleted("GET", url, init).orThrow()
        if (!(fetched.response.ok as Boolean)) {
            // An ApiError carrying the status, never a bare error(): a display site reads a bare throwable as "the
            // server could not be reached" (see webapp/CLAUDE.md), which a 403 or a 404 is not.
            throw ApiError(
                message = "GET $url failed with status ${fetched.response.status}",
                fromFragment = false,
                status = (fetched.response.status as? Number)?.toInt(),
                errorCode = null,
                traceId = null,
            )
        }
        return jsonBody("GET", url, fetched.text).orThrow()
    }

}

/**
 * The `results` map of `/schema/endpoints` (or `/schema/endpoint`) as a [Catalog]. One reader, so the two feeds
 * cannot drift in how they parse the shared shape (issue #489 added `filtersAvailable`, #564 `cfacts`, #585
 * `fieldLayouts`). A pure top-level function, like the UI-config mappers, so `jsNodeTest` covers it without a server.
 */
fun parseCatalog(results: Map<String, Any?>): Catalog = Catalog(
    endpoints = results[EI.endpoints].toJsonListOfMaps().map { toEndpointInfo(it) },
    defs = results[SCH.dDefs].toJsonMapOrEmpty(),
    // Default true when the field is absent (an older node, a hand-built store): the safe reading is that
    // filtering is allowed, and the endpoints the response already carries are what the page can show.
    filtersAvailable = results[EI.filtersAvailable] as? Boolean ?: true,
    // name -> present (issue #564); each value is a wire boolean. Absent on an older node -> empty -> no
    // gating, which is the safe reading since the backend enforces the real condition regardless.
    cfacts = results[EI.cfacts].toJsonMapOrEmpty().mapValues { it.value == true },
    // typeName -> layout (issue #585), read by the same strict kernel parser the boot ran. Absent -> empty ->
    // every type renders from its schema alone, which is what a node that sends no layouts means.
    fieldLayouts = parseDeliveredLayouts(results[EI.fieldLayouts]),
)

private fun toEndpointInfo(m: Map<String, Any?>): EndpointInfo = EndpointInfo(
    path = m[EI.path] as? String ?: "",
    method = m[EI.method] as? String ?: "",
    kind = m[EI.kind] as? String ?: "",
    namespace = m[EI.namespace] as? String ?: "",
    description = m[EI.description] as? String,
    inputSchema = m[EI.inputSchema].toJsonMapOrEmpty(),
    outputSchema = m[EI.outputSchema].toJsonMapOrEmpty(),
    publicApi = m[EI.publicApi] as? Boolean ?: false,
    tags = m[EI.tags].toJsonListOfStrings(),
    bareResponse = m[EI.bareResponse] as? Boolean ?: false,
)

/**
 * Whether [v] is a browser `File` — what the schema form's file picker emits for a binary field, as opposed to
 * the strings and numbers every other widget produces.
 *
 * Duck-typed rather than an `instanceof File`, because the value crosses from a `dynamic` DOM event into a
 * Kotlin `Map<String, Any?>` and the wrapper type it lands as is not worth pinning: a thing with a name, a
 * size and a `slice` is a `Blob`/`File` for the purpose at hand, which is deciding whether `FormData.append`
 * should treat it as a part or as text.
 */
fun isBrowserFile(v: Any?): Boolean {
    // Null first, and not as part of the chain below: `typeof null` is "object", so a null clears the type
    // test and then throws on the very next term. The parameter is `Any?` and every caller reads it out of a
    // `Map<String, Any?>`, so null is an ordinary value here -- a key the form does not hold, or one whose
    // value is JSON null -- rather than a case that should never arise (issue #260).
    if (v == null) {
        return false
    }
    val d = v.asDynamic()
    return jsTypeOf(d) == "object" && d.name != undefined && d.size != undefined && jsTypeOf(d.slice) == "function"
}

/** A picked file's name and size, for showing what was chosen where the file itself cannot go. ASCII only:
 *  this lands in a JSON preview, and the formatter escapes anything else into `\uXXXX` noise.
 *
 *  Only meaningful for a value [isBrowserFile] accepted, which is the gate both callers apply: this reads
 *  `name` and `size` straight off the value, so anything else yields "(file: undefined, undefined bytes)" at
 *  best and throws at worst. Keeping the precondition rather than inventing a fallback is deliberate -- there
 *  is no honest label for a non-file, and a plausible-looking one would hide the miswiring that produced it. */
fun browserFileLabel(v: Any?): String {
    val d = v.asDynamic()
    return "(file: ${d.name}, ${d.size} bytes)"
}

/**
 * Starts a browser download of [url]. Uses a transient anchor rather than navigating: a response carrying
 * `Content-Disposition: attachment` downloads without leaving the page, but an *error* response (a JSON 404)
 * would navigate the console away — and the whole point of a download button is not to lose your place. The
 * empty `download` attribute asks for a download while leaving the filename to the server's header.
 */
fun startDownload(url: String) {
    js(
        """
        (function () {
            var a = document.createElement('a');
            a.href = url;
            a.download = '';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        })()
        """,
    )
}



